/**
 * ExportButton Component
 * Button to trigger CSV export with download functionality
 *
 * @module MetricDataDevTool/components/ExportButton
 */

import React from 'react';
import { Download as DownloadIcon } from '@mui/icons-material';
import AdminButton from 'components/common/AdminButton';
import { exportToCSV } from '../services/csvExportService';
import { format } from 'date-fns';

/**
 * ExportButton component
 * @param {Object} props - Component props
 * @param {Array} props.data - Data to export
 * @param {string[]} props.selectedMetrics - Selected metrics
 * @param {Object} props.dateRange - Date range {from: Date, to: Date}
 * @param {Object} props.aggregationConfig - Aggregation configuration
 * @param {boolean} [props.disabled=false] - Whether button is disabled
 * @param {Function} [props.onExport] - Callback after successful export
 * @param {Function} [props.onError] - Callback on export error
 */
export default function ExportButton({
  data = [],
  selectedMetrics = [],
  dateRange,
  aggregationConfig,
  disabled = false,
  onExport,
  onError
}) {
  const handleExport = () => {
    try {
      if (!data || data.length === 0) {
        throw new Error('No data to export');
      }

      if (!dateRange?.from || !dateRange?.to) {
        throw new Error('Date range is required');
      }

      const startDate = format(dateRange.from, 'yyyy-MM-dd');
      const endDate = format(dateRange.to, 'yyyy-MM-dd');

      exportToCSV({
        data,
        selectedMetrics,
        startDate,
        endDate,
        aggregationConfig
      });

      if (onExport) {
        onExport();
      }
    } catch (error) {
      console.error('CSV export error:', error);
      if (onError) {
        onError(error.message);
      }
    }
  };

  const isDisabled = disabled || !data || data.length === 0;

  return (
    <AdminButton
      variant="contained"
      startIcon={<DownloadIcon />}
      onClick={handleExport}
      disabled={isDisabled}
    >
      Export to CSV
    </AdminButton>
  );
}
